// Shared contact-field validators for the storefront quote-request form
// (kickoffs/contact-field-validation.md). One module, used by BOTH the
// client (embed bundle) and the server routes, so the two sides always
// reach the same verdict — client validation alone is bypassable (the
// POST route accepts arbitrary JSON from any caller).
//
// Zero imports/deps: safe to pull into the embed bundle (see the @/lib
// bundling constraint noted at embed/index.ts:80 — no pg/redis).
//
// Governing rule (OWNER DECISION 2026-08-03): be exact where the format
// is knowable, permissive where it isn't.
//   - Email is always a typo-catcher shape check, never RFC 5322.
//   - Phone is region-aware: inside the NANP (US/CA + territories), the
//     numbering plan is published and unambiguous, so it's enforced
//     strictly. Outside NANP, the format isn't reliably knowable from a
//     string alone, so it falls back to a permissive shape check. A false
//     reject costs a real customer; a false accept costs a slightly
//     dirty CRM record — that asymmetry still governs everything outside
//     NANP.

/** Conservative shape check: a local part, `@`, and a domain with at
 *  least one dot — no whitespace anywhere. Not an RFC 5322 implementation;
 *  deliberately permissive so plus-addressing and long/new TLDs pass. */
export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

// NANP (North American Numbering Plan) regions — US/CA plus the other
// territories that share the +1 country code. Scoped to this lane's one
// question ("does this region get strict NANP rules?"); phone-e164.ts
// keeps its own separate (unexported) copy for E.164 normalization,
// deliberately left untouched here (kickoffs/contact-field-
// validation.md explicitly keeps that file out of this lane's scope).
const NANP_REGIONS = new Set([
  "US", "CA", "AG", "AI", "AS", "BB", "BM", "BS", "DM", "DO", "GD", "GU",
  "JM", "KN", "KY", "LC", "MP", "MS", "PR", "SX", "TC", "TT", "VC", "VG", "VI",
]);

/** Resolve the region to validate a phone number against, per the
 *  owner-instructed fallback chain: the request's own shipping country
 *  → the shop's configured (default/primary allowed) country → "US".
 *  Both inputs are ISO 3166-1 alpha-2, case-insensitive. */
export function resolvePhoneRegion(
  shippingCountry: string | null | undefined,
  shopDefaultCountry: string | null | undefined,
): string {
  const shipping = (shippingCountry || "").trim().toUpperCase();
  if (shipping) return shipping;
  const shopDefault = (shopDefaultCountry || "").trim().toUpperCase();
  if (shopDefault) return shopDefault;
  return "US";
}

/** Real NANP shape, nothing beyond the published plan: exactly 10 digits,
 *  or 11 digits starting with country code "1". NPA (area code) and NXX
 *  (exchange) first digits must be 2-9 (0/1 are reserved for service
 *  codes). N11 service codes (911, 411, …) are rejected as area codes. */
function isValidNanpPhone(digits: string): boolean {
  let national: string;
  if (digits.length === 10) national = digits;
  else if (digits.length === 11 && digits.startsWith("1")) national = digits.slice(1);
  else return false;

  const npa = national.slice(0, 3);
  const nxx = national.slice(3, 6);
  if (npa[0] < "2" || npa[0] > "9") return false;
  if (nxx[0] < "2" || nxx[0] > "9") return false;
  if (npa[1] === "1" && npa[2] === "1") return false; // N11 service code as an area code
  return true;
}

/** Permissive E.164 shape check for anything outside NANP: 8-15 digits
 *  (the same bounds phone-e164.ts's normalizer already uses), reject
 *  all-identical-digit junk. No country assumption — a real
 *  international number must pass. */
function isValidGenericPhone(digits: string): boolean {
  if (digits.length < 8 || digits.length > 15) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  return true;
}

/** Region-aware phone validator. `region` should be the caller's
 *  best-resolved expected region (see resolvePhoneRegion) — an ISO
 *  3166-1 alpha-2 code, case-insensitive. A NANP region gets the strict
 *  numbering-plan check; anything else (including an unresolved/empty
 *  region) gets the permissive shape check — the safe direction when
 *  the region can't be confidently placed. */
export function isValidPhone(v: string, region?: string | null): boolean {
  const trimmed = v.trim();
  if (!trimmed) return false;
  if (/[a-zA-Z]/.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return false;
  if (region && NANP_REGIONS.has(region.trim().toUpperCase())) {
    return isValidNanpPhone(digits);
  }
  return isValidGenericPhone(digits);
}
